import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'

import { assetUrl } from 'utils/url'

export type EvaluationResultProps = {
  dinas: string
  predicate: string
  performancePracticeLabel?: React.ReactNode
  workBehaviorLabel?: React.ReactNode
  subordinate: {
    name: string
    nip: string
    departmentLevel: string
    role: string
    schoolName: string
  }
  supervisor: {
    name: string
    nip: string
    departmentLevel: string
    role: string
    schoolName: string
  }
  title: string
  year: number
}

export function EvaluationResult({
  dinas,
  predicate,
  performancePracticeLabel,
  workBehaviorLabel,
  subordinate,
  supervisor,
  title,
  year,
}: EvaluationResultProps) {
  return (
    <section className="bg-white rounded-3xl overflow-hidden w-full">
      <header
        className={clsx(
          'relative overflow-hidden flex items-center gap-6 px-9 py-6',
          'bg-opacity-50 bg-blue-90 bg-gradient-to-r from-blue-90 to-blue-80'
        )}
      >
        <img
          alt=""
          role="presentation"
          src={assetUrl('/images/pengelolaan-kinerja/confetti.png')}
          className="absolute right-0 top-0"
          width={144}
        ></img>
        <div className="flex h-11 items-center gap-3 self-center">
          <img
            src={assetUrl('/images/kemendikbud2x.png')}
            width={42}
            height={42}
            className="max-h-full"
            alt=""
          />
          <img
            src={assetUrl('/icon/e-pengelolaan-kinerja-logo-secondary.png')}
            width={124}
            height={32}
            alt=""
          />
        </div>
        <Text variant="heading-md" as="h2" color="inverse">
          {title}
        </Text>
      </header>
      <div className="max-w-3xl p-9">
        <>
          <div className="flex">
            <section className="space-y-1 w-6/12">
              <Text variant="heading-sm" as="h3">
                Nama Instansi
              </Text>
              <Text>{dinas}</Text>
            </section>
            <section className="space-y-1">
              <Text variant="heading-sm">Periode Penilaian</Text>
              <Text>1 Januari sampai dengan 31 Desember {year}</Text>
            </section>
          </div>
          <hr className="my-9" />
          <section className="space-y-1">
            <Text variant="heading-sm" as="h3">
              Pegawai Yang Dinilai
            </Text>
            <dl className="grid grid-cols-2 gap-y-1">
              <Text as="dt">Nama</Text>
              <Text as="dd">: {subordinate.name || '-'}</Text>
              <Text as="dt">NIP</Text>
              <Text as="dd">: {subordinate.nip || '-'}</Text>
              <Text as="dt">Jabatan</Text>
              <Text as="dd">: {subordinate.role || '-'}</Text>
              <Text as="dt">Unit Kerja</Text>
              <Text as="dd">: {subordinate.schoolName || '-'}</Text>
            </dl>
          </section>
          <hr className="my-9" />
          <section className="space-y-2">
            <Text variant="heading-sm" as="h3">
              Pejabat Penilai Kinerja
            </Text>
            <dl className="grid grid-cols-2 gap-y-2">
              <Text as="dd">Nama</Text>
              <Text as="dt">: {supervisor.name || '-'}</Text>
              <Text as="dd">NIP</Text>
              <Text as="dt">: {supervisor.nip || '-'}</Text>
              <Text as="dd">Jabatan</Text>
              <Text as="dt">: {supervisor.role || '-'}</Text>
              <Text as="dd">Unit Kerja</Text>
              <Text as="dt">: {supervisor.schoolName || '-'}</Text>
            </dl>
          </section>
          <hr className="my-9" />
          <section className="space-y-4">
            <div className="flex space-x-2 items-center">
              <Text variant="heading-sm" as="h3">
                Predikat Kinerja
              </Text>
              <Text
                className="h-6 px-2 rounded-full inline-flex items-center"
                variant="body-sm-bold"
                color="inverse"
              >
                Tahunan
              </Text>
            </div>
            <div className="rounded border border-subdued ">
              {performancePracticeLabel && (
                <div className="border-b border-subdued flex items-center px-6 py-4 space-x-4 justify-between">
                  <div className="space-y-1">
                    <Text variant="heading-sm">Praktik Kinerja</Text>
                    <Text variant="body-sm" color="disabled">
                      Berdasarkan dokumen Tindak Lanjut dan Refleksi Tindak
                      Lanjut
                    </Text>
                  </div>
                  <div className="flex-shrink-0 w-44">
                    {performancePracticeLabel}
                  </div>
                </div>
              )}
              {workBehaviorLabel && (
                <div className="border-b border-subdued flex items-center px-6 py-4 space-x-4 justify-between">
                  <div className="space-y-1">
                    <Text variant="heading-sm">Perilaku Kerja</Text>
                    <Text variant="body-sm" color="disabled">
                      Berdasarkan penilaian terhadap 7 aspek Perilaku Kerja
                    </Text>
                  </div>
                  <div className="flex-shrink-0 w-44">{workBehaviorLabel}</div>
                </div>
              )}
              <div className="flex items-center justify-between bg-surface-selected-default px-6 py-4 rounded-b">
                <Text variant="heading-md" className="w-9/12">
                  Predikat Kinerja
                </Text>
                <Text variant="heading-md" className="flex-shrink-0 w-44">
                  {predicate || '-'}
                </Text>
              </div>
            </div>
          </section>
        </>
        <footer className="mt-9 space-y-4">
          <section>
            <Text variant="body-sm" as="h3">
              <strong> Catatan:</strong>
            </Text>

            <ol className="list list-decimal px-4">
              <Text as="li" variant="body-sm">
                Dokumen ini tidak perlu ditandatangani.
              </Text>
              <Text as="li" variant="body-sm">
                Predikat Kinerja pada dokumen ini akan digunakan untuk konversi
                Angka Kredit di e-Kinerja BKN (Badan Kepegawaian Negara).
              </Text>
            </ol>
          </section>
        </footer>
      </div>
    </section>
  )
}
